'use client';

import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "children", "id", "aria-labelledby", "className"];
import React, { useContext } from 'react';
import cx from 'classnames';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { generateEncoreTabId } from "../TabItem/index.mjs";
import { TabsContext } from "../Tabs/index.mjs";
import { StyledTabPanel } from "./TabPanel.mjs";
import { BrowserDefaultFocusStyleContext } from "../../contexts/BrowserDefaultFocusStyleContext.mjs";
import { KeyboardDetectionContext } from "../../contexts/KeyboardDetectionContext.mjs";
import { textClass } from "../../styles/layout-theme.mjs";
function hasInteractiveElements(node) {
  // This list was taken from: https://github.com/focus-trap/tabbable/blob/f276e03f7e836b5c65b778cfeadb59881c52cbd2/src/index.mjs#L6
  var candidateSelectors = ['input:not([inert])', 'select:not([inert])', 'textarea:not([inert])', 'a[href]:not([inert])', 'button:not([inert])', '[tabindex]:not(slot):not([inert])', 'audio[controls]:not([inert])', 'video[controls]:not([inert])', '[contenteditable]:not([contenteditable="false"]):not([inert])', 'details>summary:first-of-type:not([inert])', 'details:not([inert])'];
  var interactiveElements = node.querySelectorAll(candidateSelectors.join(', '));
  return interactiveElements.length > 0;
}

/**
 * TabPanel is the content that is displayed when the associated TabItem is selected.
 *
 * This component is meant to be used in conjunction with the other Tab components. Please refer to {@link https://encore-web.spotify.net/?path=/docs/components-tabs--docs Tabs} for a more complete example.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-tabs-tabpanel--docs) |
 * [Encore site](https://encore.spotify.net/web/components/tabs) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/TabPanel)
 */
export var TabPanel = /*#__PURE__*/React.forwardRef(function TabPanel(_ref, ref) {
  var value = _ref.value,
    children = _ref.children,
    id = _ref.id,
    ariaLabelledby = _ref['aria-labelledby'],
    className = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useContext = useContext(KeyboardDetectionContext),
    isUsingKeyboard = _useContext.isUsingKeyboard;
  var _useContext2 = useContext(BrowserDefaultFocusStyleContext),
    useBrowserDefaultFocusStyle = _useContext2.useBrowserDefaultFocusStyle;
  var _useContext3 = useContext(TabsContext),
    selectedTabItem = _useContext3.selectedTabItem,
    tabPanel = _useContext3.tabPanel;
  var encoreTabId = generateEncoreTabId(value);
  var handleAddingTabItemRef = function handleAddingTabItemRef(node) {
    if (node && tabPanel) {
      if (!hasInteractiveElements(node)) {
        // If there are no interactive elements, set tabPanel so it can be focused on ArrowDown
        tabPanel.set(encoreTabId, node);
      }
    } else if (tabPanel) {
      tabPanel["delete"](encoreTabId);
    }
    // Make sure that we are handling the ref passed from the customer
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  };

  /**
   * Check to see that Tabs is being used (selectedTabItem should be defined)
   * If selectedTabItem is undefined, just render the TabPanel since the customer is probably implementing their own stateful logic
   */
  if (selectedTabItem === undefined || selectedTabItem === encoreTabId) {
    return /*#__PURE__*/React.createElement(StyledTabPanel, Object.assign({
      ref: handleAddingTabItemRef,
      className: cx(textClass('bodyMedium'), className),
      "data-encore-id": ENCORE_DATA_IDS.TabPanel
      // Necessary to focus on ArrowDown
      ,
      tabIndex: -1,
      role: "tabpanel",
      id: id !== null && id !== void 0 ? id : "".concat(value, "-panel"),
      "aria-labelledby": ariaLabelledby !== null && ariaLabelledby !== void 0 ? ariaLabelledby : encoreTabId,
      $isUsingKeyboard: isUsingKeyboard,
      $useBrowserDefaultFocusStyle: useBrowserDefaultFocusStyle
    }, props), children);
  }
  return null;
});